import {
  ConversationMenu,
  useConversationMenu,
} from "@app/components/assistant/conversation/ConversationMenu";
import { useConversationSidePanelContext } from "@app/components/assistant/conversation/ConversationSidePanelContext";
import { EditConversationTitleDialog } from "@app/components/assistant/conversation/EditConversationTitleDialog";
import { ThirdPartyAssistantBadge } from "@app/components/assistant/conversation/ThirdPartyAssistantBadge";
import { getParentConversationTitleLabel } from "@app/components/assistant/conversation/utils";
import { HuddleButton } from "@app/components/live/HuddleButton";
import { AppLayoutTitle } from "@app/components/sparkle/AppLayoutTitle";
import {
  useConversation,
  useConversationParticipants,
} from "@app/hooks/conversations";
import { useActiveConversationId } from "@app/hooks/useActiveConversationId";
import { useAuth, useFeatureFlags } from "@app/lib/auth/AuthContext";
import { useSpaceInfo } from "@app/lib/swr/spaces";
import { useIsMobile } from "@app/lib/swr/useIsMobile";
import { getConversationRoute, getPodRoute } from "@app/lib/utils/router";
import { getConversationDisplayTitle } from "@app/types/assistant/conversation";
import { DETAILS_SIDE_PANEL_TYPE } from "@app/types/conversation_side_panel";
import type { WorkspaceType } from "@app/types/user";
import {
  ArrowLeft,
  Breadcrumbs,
  type BreadcrumbsItem,
  Button,
  Chip,
  CoinsStacked01,
  DotsHorizontal,
  GitBranch01,
  LayoutRight,
  Tooltip,
} from "@dust-tt/sparkle";
import { useState } from "react";

const BREADCRUMB_MIDDLE_TRUNCATE_LENGTH = 35;
const DESKTOP_TITLE_TRUNCATE_LENGTH = 120;
const MOBILE_FORKED_TITLE_TRUNCATE_LENGTH = 35;

export function ConversationTitle({ owner }: { owner: WorkspaceType }) {
  const activeConversationId = useActiveConversationId();
  const { user } = useAuth();
  const { hasFeature } = useFeatureFlags();
  const { togglePanel, currentPanel } = useConversationSidePanelContext();
  const isDetailsPanelOpen = currentPanel === DETAILS_SIDE_PANEL_TYPE;
  const { conversation } = useConversation({
    conversationId: activeConversationId,
    workspaceId: owner.sId,
  });
  const { spaceInfo } = useSpaceInfo({
    workspaceId: owner.sId,
    spaceId: conversation?.spaceId ?? null,
  });
  const isMobile = useIsMobile();
  const { conversationParticipants } = useConversationParticipants({
    conversationId: activeConversationId,
    workspaceId: owner.sId,
  });

  const [showRenameDialog, setShowRenameDialog] = useState(false);

  const {
    isMenuOpen,
    menuTriggerPosition,
    handleRightClick,
    handleMenuOpenChange,
  } = useConversationMenu();

  const currentTitle = conversation
    ? getConversationDisplayTitle(conversation)
    : "";

  if (!activeConversationId) {
    return null;
  }

  const spaceId = conversation?.spaceId;
  const isProjectConversation = !!spaceId;
  const isLoading = isProjectConversation && !spaceInfo;
  const forkedFrom = conversation?.forkingData?.forkedFrom;
  const isMobileForkedConversation = isMobile && !!forkedFrom;

  const breadcrumbItems: BreadcrumbsItem[] = [];

  if (spaceId && spaceInfo) {
    breadcrumbItems.push({
      icon: isMobile ? undefined : ArrowLeft,
      label: spaceInfo.name,
      href: getPodRoute(owner.sId, spaceId),
    });
  }

  if (!isLoading) {
    breadcrumbItems.push({
      label: currentTitle || "New Conversation",
      onClick: () => setShowRenameDialog(true),
    });
  }

  const hasThirdPartyAssistant =
    conversationParticipants?.agents.some((agent) => agent.isRelay) ?? false;

  const ForkedFromChip = () => {
    if (!forkedFrom) {
      return null;
    }

    const chipLabel = getParentConversationTitleLabel(forkedFrom);
    const tooltipLabel = `Branched from '${chipLabel}'`;

    return (
      <div className="flex h-9 shrink-0 items-center">
        <Tooltip
          label={tooltipLabel}
          tooltipTriggerAsChild
          trigger={
            <span className="inline-flex h-9 items-center">
              <Chip
                className={
                  isMobile
                    ? "shrink-0 dd-privacy-mask [&>span]:sr-only"
                    : "max-w-44 shrink-0 dd-privacy-mask"
                }
                color="primary"
                href={getConversationRoute(
                  owner.sId,
                  forkedFrom.parentConversationId
                )}
                icon={GitBranch01}
                label={isMobile ? tooltipLabel : chipLabel}
                size="mini"
              />
            </span>
          }
        />
      </div>
    );
  };

  return (
    <AppLayoutTitle>
      <div
        className="grid h-full min-w-0 max-w-full grid-cols-[1fr_auto] items-center gap-3"
        onContextMenu={handleRightClick}
      >
        <div
          className={
            isMobileForkedConversation
              ? "flex min-w-0 items-center gap-2 overflow-hidden scrollbar-hide"
              : "flex min-w-0 items-center gap-2 overflow-x-auto scrollbar-hide"
          }
        >
          <div
            className={
              isMobileForkedConversation
                ? "flex min-w-0 flex-1 items-center overflow-hidden"
                : "flex min-w-0 items-center"
            }
          >
            <Breadcrumbs
              items={breadcrumbItems}
              className="dd-privacy-mask"
              truncateLengthMiddle={BREADCRUMB_MIDDLE_TRUNCATE_LENGTH}
              truncateLengthEnd={
                isMobileForkedConversation
                  ? MOBILE_FORKED_TITLE_TRUNCATE_LENGTH
                  : DESKTOP_TITLE_TRUNCATE_LENGTH
              }
            />
          </div>
          <ForkedFromChip />
          {hasThirdPartyAssistant && <ThirdPartyAssistantBadge />}
        </div>
        <EditConversationTitleDialog
          isOpen={showRenameDialog}
          onClose={() => setShowRenameDialog(false)}
          owner={owner}
          conversationId={activeConversationId}
          currentTitle={currentTitle}
        />
        <div className="flex items-center gap-2">
          {hasFeature("conversation_consumption_details") && (
            <Button
              size="sm"
              label={isMobile ? undefined : "Credit usage"}
              icon={CoinsStacked01}
              variant="ghost"
              onClick={() => togglePanel({ type: "credits" })}
            />
          )}
          <HuddleButton
            owner={owner}
            conversationId={activeConversationId}
            title={currentTitle || null}
          />
          <Button
            size="sm"
            icon={LayoutRight}
            variant={isDetailsPanelOpen ? "ghost-secondary" : "ghost"}
            tooltip={isDetailsPanelOpen ? "Hide chat details" : "In this chat"}
            aria-expanded={isDetailsPanelOpen}
            onClick={() => togglePanel({ type: "details" })}
          />
          <ConversationMenu
            activeConversationId={activeConversationId}
            conversation={conversation}
            owner={owner}
            trigger={({ isPendingAction }) => (
              <Button
                size="sm"
                variant="ghost"
                icon={DotsHorizontal}
                aria-label="Conversation menu"
                isLoading={isPendingAction}
                disabled={
                  activeConversationId === null ||
                  conversation === null ||
                  user === null ||
                  isPendingAction
                }
              />
            )}
            isConversationDisplayed={true}
            isOpen={isMenuOpen}
            onOpenChange={handleMenuOpenChange}
            triggerPosition={menuTriggerPosition}
          />
        </div>
      </div>
    </AppLayoutTitle>
  );
}
